/** @fileoverview Builds the ordered activity timeline for one chat message. */

import { toSkillReadData } from '../../lib/skillRead';
import type { AskQuestionData, FabrikAskQuestionPart, FabrikUIMessage } from '../../types/chat';
import { getActivityEntries } from './toolActivity';
import type { SkillActivityEntry, TimelineItem, ToolActivityEntry, ToolGroupItem } from './types';

const progressToolName = (part: FabrikUIMessage['parts'][number]) =>
  part.type === 'dynamic-tool'
    ? part.toolName
    : part.type.startsWith('tool-')
    ? part.type.slice(5)
    : undefined;

const getProgressLabel = (message: FabrikUIMessage) => {
  for (let index = message.parts.length - 1; index >= 0; index -= 1) {
    const part = message.parts[index];
    if (progressToolName(part) !== 'reportProgress') continue;
    const metadata = 'toolMetadata' in part ? part.toolMetadata : undefined;
    const activityLabel =
      metadata && typeof metadata.activityLabel === 'string' ? metadata.activityLabel.trim() : '';
    if (activityLabel) return activityLabel;
  }
  return undefined;
};

const questionsIn = (message: FabrikUIMessage) => {
  // Map updates preserve the first position while keeping the latest streamed state.
  const byId = new Map<string, FabrikAskQuestionPart>();
  for (const part of message.parts) {
    if (part.type === 'data-ask-question') byId.set(part.data.id, part);
  }
  return byId;
};

const buildTimeline = (
  message: FabrikUIMessage,
  toolEntries: ToolActivityEntry[],
  skillEntries: Map<string, SkillActivityEntry>,
  mergeToolsAcrossAssistantText: boolean
): TimelineItem[] => {
  const timeline: TimelineItem[] = [];
  const seenTools = new Set<string>();
  const seenSkills = new Set<string>();
  const toolEntriesById = new Map(toolEntries.map((entry) => [entry.key, entry]));
  let currentToolGroup: ToolGroupItem | undefined;
  const startToolGroup = (key: string) => {
    const group: ToolGroupItem = { kind: 'tool-group', key, entries: [] };
    timeline.push(group);
    currentToolGroup = group;
    return group;
  };

  message.parts.forEach((part, index) => {
    if ('toolCallId' in part && typeof part.toolCallId === 'string') {
      const entry = toolEntriesById.get(part.toolCallId);
      if (entry && !seenTools.has(entry.key)) {
        seenTools.add(entry.key);
        (currentToolGroup ?? startToolGroup(`tools-${index}`)).entries.push(entry);
      }
      return;
    }

    // Ask-question results render in the assistant response, not the thinking timeline.
    if (part.type === 'data-ask-question') {
      currentToolGroup = undefined;
      return;
    }

    // Streamed response text is rendered outside the activity timeline; keep tools together across it.
    if (part.type === 'text' && part.text) {
      if (!mergeToolsAcrossAssistantText) currentToolGroup = undefined;
      return;
    }
    if (part.type === 'reasoning' && part.text) {
      currentToolGroup = undefined;
      timeline.push({ kind: 'reasoning', key: `reasoning-${index}`, text: part.text });
    } else if (part.type === 'data-skill-read') {
      currentToolGroup = undefined;
      const skill = toSkillReadData(part.data);
      const entry = skill ? skillEntries.get(skill.id) : undefined;
      if (entry && !seenSkills.has(entry.key)) {
        seenSkills.add(entry.key);
        timeline.push({ kind: 'skill', key: `skill-${entry.key}`, entry });
      }
    }
  });

  // Persisted activity may have no UI part, so retain it in a trailing group.
  const savedOnlyTools = toolEntries.filter((entry) => !seenTools.has(entry.key));
  if (savedOnlyTools.length) {
    timeline.push({ kind: 'tool-group', key: 'saved-tools', entries: savedOnlyTools });
  }
  return timeline;
};

export const getMessageActivity = (
  message: FabrikUIMessage,
  mergeToolsAcrossAssistantText = false
) => {
  const activity = message.metadata?.activity;
  const entries = getActivityEntries(message, activity?.tools);
  const questionsById = questionsIn(message);
  const questions: AskQuestionData[] = [...questionsById.values()].map((part) => part.data);
  const pendingQuestions = questions.filter((question) => question.phase === 'pending');
  const toolEntries = entries.filter((entry): entry is ToolActivityEntry => entry.kind === 'tool');
  const skillEntries = new Map(
    entries
      .filter((entry): entry is SkillActivityEntry => entry.kind === 'skill')
      .map((entry) => [entry.key, entry])
  );

  return {
    activity,
    entries,
    progressLabel: getProgressLabel(message),
    questions,
    pendingQuestions,
    toolEntries,
    timeline: buildTimeline(message, toolEntries, skillEntries, mergeToolsAcrossAssistantText),
  };
};
